import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  PenLine, BookOpen, Users, Trophy, Star, MessageCircle, 
  ArrowRight, Sparkles, Heart, Shield, Baby, ScrollText, 
  Wand2, Feather, Layers, Eye, Megaphone, Pin, Newspaper,
  Share2, Mail, Link2, Facebook
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LandingPage = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [latestWorks, setLatestWorks] = useState([]);

  useEffect(() => {
    const fetchPublicData = async () => {
      try {
        const [annRes, worksRes] = await Promise.all([
          axios.get(`${API}/announcements?limit=3`),
          axios.get(`${API}/stories?limit=4`)
        ]);
        setAnnouncements(annRes.data);
        setLatestWorks(worksRes.data);
      } catch (e) {
        // Non-critical error suppressed
      }
    };
    fetchPublicData();
  }, []);
  const features = [
    {
      icon: Trophy,
      title: "Writing Challenges",
      description: "Bi-weekly prompts judged on craft—best character development, best hook, best conceptualization. No popularity contests here."
    },
    {
      icon: MessageCircle,
      title: "Constructive Feedback",
      description: "A community dedicated to respectful, cogent feedback that helps everyone advance their talent."
    },
    {
      icon: BookOpen,
      title: "What Are You Reading?",
      description: "Stimulate discussions on books you've discovered and share recommendations with fellow writers."
    },
    {
      icon: Star,
      title: "Guest Authors",
      description: "Q&A sessions with professional authors. Ask questions, learn from the best."
    },
    {
      icon: Sparkles,
      title: "Spotlight Works",
      description: "Every week, our founders select pieces that particularly touched them to share with the community."
    },
    {
      icon: Users,
      title: "Writing Groups & Forums",
      description: "Find your tribe. Join groups, start discussions, and collaborate with like-minded writers."
    }
  ];

  const genreHighlights = [
    { icon: Baby, name: "Children's", color: "bg-pink-100 text-pink-600" },
    { icon: ScrollText, name: "Poetry", color: "bg-indigo-100 text-indigo-700" },
    { icon: Feather, name: "Literary Fiction", color: "bg-purple-100 text-purple-700" },
    { icon: Wand2, name: "Fantasy", color: "bg-violet-100 text-violet-700" },
    { icon: Heart, name: "Romance", color: "bg-red-100 text-red-500" },
    { icon: BookOpen, name: "Memoir", color: "bg-emerald-100 text-emerald-700" },
  ];

  return (
    <div className="min-h-screen" data-testid="landing-page">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <span className="overline text-brand-gold mb-4 block">Welcome to</span>
              <h1 className="font-serif text-5xl lg:text-6xl font-semibold text-text-primary leading-tight mb-6">
                Collectively Write
              </h1>
              <p className="text-xl text-text-secondary leading-relaxed mb-8 max-w-xl">
                A creative sanctuary for poets, fiction writers, screenwriters, essayists, 
                and every voice in between. Share your work, sharpen your craft, and connect 
                with writers who value quality over clicks.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/register" data-testid="hero-join-button">
                  <Button className="btn-accent text-lg px-8 py-6 gap-2">
                    Join Our Community
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/stories" data-testid="hero-browse-button">
                  <Button variant="outline" className="btn-outline text-lg px-8 py-6">
                    Browse Works
                  </Button>
                </Link>
              </div>
            </div>
            
            <div className="relative animate-fade-in stagger-2">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-soft-md">
                <img 
                  src="https://images.pexels.com/photos/6980880/pexels-photo-6980880.jpeg"
                  alt="Writer at desk"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-4 shadow-soft-md">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-brand-accent/10 flex items-center justify-center">
                    <Heart className="w-6 h-6 text-brand-accent" />
                  </div>
                  <div>
                    <p className="font-semibold text-text-primary">Quality Over Quantity</p>
                    <p className="text-sm text-text-muted">Craft-focused community</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-surface-alt">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="overline text-brand-gold mb-4 block">Our Story</span>
          <h2 className="font-serif text-3xl lg:text-4xl font-semibold text-text-primary mb-8">
            Built by Writers, for Writers
          </h2>
          <div className="font-reading text-lg text-text-secondary leading-loose space-y-6 text-left">
            <p>
              We are refugees from a writing site that was overrun with members who clique-voted 
              their way to the top of contests. They seemed to prefer this method over writing well, 
              reviewing articulately, or improving their skills.
            </p>
            <p>
              So we sighed, rolled our eyes, and decided it was time to launch a <strong>BS-free, 
              quality writing site</strong> of our own.
            </p>
            <p>
              It's such a simple concept: <em>You show me yours, and I'll show you mine.</em> Then 
              collectively, by sharing our thoughts and making suggestions, we'll ALL advance our 
              talent and build a community of creative, like-minded souls with a penchant for the 
              written word.
            </p>
          </div>
          <div className="flex items-center justify-center gap-8 mt-12">
            <div className="text-center">
              <p className="font-serif text-lg font-medium text-text-primary">Heather Wickham</p>
              <p className="text-sm text-text-muted">Co-Founder</p>
            </div>
            <div className="w-px h-12 bg-brand-primary/20"></div>
            <div className="text-center">
              <p className="font-serif text-lg font-medium text-text-primary">Rachelle Allen</p>
              <p className="text-sm text-text-muted">Co-Founder</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="overline text-brand-gold mb-4 block">What We Offer</span>
            <h2 className="font-serif text-3xl lg:text-4xl font-semibold text-text-primary">
              Fun & Enriching Pages
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={feature.title}
                className="bg-white rounded-xl p-8 border border-brand-primary/10 card-hover animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-14 h-14 rounded-xl bg-brand-primary/10 flex items-center justify-center mb-6">
                  <feature.icon className="w-7 h-7 text-brand-primary" />
                </div>
                <h3 className="font-serif text-xl font-semibold text-text-primary mb-3">
                  {feature.title}
                </h3>
                <p className="text-text-secondary leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Writing Sections - All Writers Welcome */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="overline text-brand-gold mb-4 block">All Writers Welcome</span>
            <h2 className="font-serif text-3xl lg:text-4xl font-semibold text-text-primary mb-4">
              A Home for Every Genre
            </h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              From children's stories to poetry, memoirs to fantasy—every voice belongs here.
            </p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-4 mb-10">
            {genreHighlights.map((genre) => (
              <Link 
                key={genre.name}
                to={`/stories?genre=${encodeURIComponent(genre.name)}`}
                className={`flex items-center gap-2 px-5 py-3 rounded-full ${genre.color} font-medium transition-transform hover:scale-105`}
              >
                <genre.icon className="w-5 h-5" />
                {genre.name}
              </Link>
            ))}
          </div>
          
          <div className="text-center">
            <Link to="/sections" data-testid="browse-all-sections">
              <Button className="btn-accent gap-2">
                <Layers className="w-4 h-4" />
                Browse All Writing Sections
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Guidelines Section */}
      <section className="py-20 bg-brand-dark text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Shield className="w-12 h-12 text-brand-gold mx-auto mb-6" />
          <h2 className="font-serif text-3xl lg:text-4xl font-semibold mb-6">
            A Safe Space for Creativity
          </h2>
          <p className="text-white/80 text-lg leading-relaxed mb-8">
            We welcome most genres but, for now, are steering clear of content that 
            too often leads to less-than-polite discussions:
          </p>
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <span className="px-4 py-2 rounded-full bg-white/10 text-white/90">No Erotica</span>
            <span className="px-4 py-2 rounded-full bg-white/10 text-white/90">No Politics</span>
            <span className="px-4 py-2 rounded-full bg-white/10 text-white/90">Faith Stories Welcome (Story-Focused)</span>
          </div>
          <p className="text-white/60 text-sm">
            We will always welcome your feedback and quickly respond to any questions or comments.
          </p>
        </div>
      </section>

      {/* Writer Ownership & Originality Promise */}
      <section className="py-16 bg-surface-alt border-y border-brand-primary/10" data-testid="ownership-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="flex items-start gap-4 p-6 bg-white rounded-xl border border-brand-primary/10">
              <div className="w-12 h-12 rounded-full bg-brand-primary/10 flex items-center justify-center flex-shrink-0">
                <Shield className="w-6 h-6 text-brand-primary" />
              </div>
              <div>
                <h3 className="font-serif text-xl font-semibold text-text-primary mb-2">
                  Writers Own 100% of Their Work
                </h3>
                <p className="text-text-secondary leading-relaxed">
                  Your words are yours—always. Collectively Write claims zero rights to anything you create. 
                  You retain full copyright and may remove your work at any time, no questions asked.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-6 bg-white rounded-xl border border-brand-primary/10">
              <div className="w-12 h-12 rounded-full bg-brand-gold/10 flex items-center justify-center flex-shrink-0">
                <Feather className="w-6 h-6 text-brand-gold" />
              </div>
              <div>
                <h3 className="font-serif text-xl font-semibold text-text-primary mb-2">
                  Original Writing Only
                </h3>
                <p className="text-text-secondary leading-relaxed">
                  We celebrate the human craft of writing. All work shared here must be original—not generated by AI. 
                  This is a space for real writers honing real skills together.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Community Board & Latest Works */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10">
            {/* Community Board */}
            <div data-testid="landing-community-board">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-[#1A3A52] flex items-center justify-center">
                  <Megaphone className="w-5 h-5 text-[#C67B5C]" />
                </div>
                <div>
                  <h3 className="font-serif text-2xl font-semibold text-text-primary">Community Board</h3>
                  <p className="text-sm text-text-muted">News & announcements from our founders</p>
                </div>
              </div>
              {announcements.length > 0 ? (
                <div className="space-y-3">
                  {announcements.map((ann) => (
                    <Link key={ann.id} to="/community-board" className="block">
                      <div className="p-4 bg-[#F7F3EF] rounded-xl border border-brand-primary/10 hover:border-brand-accent/30 transition-colors">
                        <div className="flex items-start gap-2">
                          {ann.pinned && <Pin className="w-4 h-4 text-brand-accent flex-shrink-0 mt-0.5" />}
                          <div>
                            <h4 className="font-medium text-text-primary text-sm">{ann.title}</h4>
                            <p className="text-text-secondary text-xs mt-1 line-clamp-2">{ann.content}</p>
                            <p className="text-text-muted text-xs mt-2">{ann.author_name} · {new Date(ann.created_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                  <Link to="/community-board" className="inline-flex items-center gap-1 text-sm text-brand-accent hover:underline font-medium mt-2">
                    See all announcements <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              ) : (
                <div className="p-8 bg-[#F7F3EF] rounded-xl border border-brand-primary/10 text-center">
                  <Megaphone className="w-8 h-8 text-text-muted mx-auto mb-2" />
                  <p className="text-sm text-text-muted">Announcements will appear here</p>
                </div>
              )}
            </div>

            {/* Latest Works */}
            <div data-testid="landing-latest-works">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-[#1A3A52] flex items-center justify-center">
                  <Newspaper className="w-5 h-5 text-[#C67B5C]" />
                </div>
                <div>
                  <h3 className="font-serif text-2xl font-semibold text-text-primary">New Posts</h3>
                  <p className="text-sm text-text-muted">Fresh works from our community</p>
                </div>
              </div>
              {latestWorks.length > 0 ? (
                <div className="space-y-3">
                  {latestWorks.map((work) => (
                    <Link key={work.id} to={`/story/${work.id}`} className="block">
                      <div className="flex items-start gap-3 p-4 bg-[#F7F3EF] rounded-xl border border-brand-primary/10 hover:border-brand-accent/30 transition-colors">
                        {work.cover_image ? (
                          <img src={work.cover_image} alt={work.title} className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
                        ) : (
                          <div className="w-12 h-12 rounded-lg bg-brand-primary/5 flex items-center justify-center flex-shrink-0">
                            <BookOpen className="w-5 h-5 text-brand-primary/30" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-xs text-brand-accent font-medium">{work.genre}</span>
                          </div>
                          <h4 className="font-serif font-medium text-text-primary text-sm leading-snug line-clamp-1">{work.title}</h4>
                          <p className="text-xs text-text-muted mt-1">by {work.author_name}</p>
                          <div className="flex items-center gap-3 mt-1.5 text-xs text-text-muted">
                            <span className="flex items-center gap-0.5"><Eye className="w-3 h-3" /> {work.view_count}</span>
                            <span className="flex items-center gap-0.5"><Heart className="w-3 h-3" /> {work.like_count}</span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                  <Link to="/stories" className="inline-flex items-center gap-1 text-sm text-brand-accent hover:underline font-medium mt-2">
                    Browse all works <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              ) : (
                <div className="p-8 bg-[#F7F3EF] rounded-xl border border-brand-primary/10 text-center">
                  <BookOpen className="w-8 h-8 text-text-muted mx-auto mb-2" />
                  <p className="text-sm text-text-muted">New works will appear here</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl lg:text-4xl font-semibold text-text-primary mb-6">
            Ready to Join Our Community?
          </h2>
          <p className="text-xl text-text-secondary mb-8">
            Start sharing your stories and connecting with fellow writers today.
          </p>
          <Link to="/register" data-testid="cta-join-button">
            <Button className="btn-accent text-lg px-10 py-6 gap-2">
              <PenLine className="w-5 h-5" />
              Get Started Free
            </Button>
          </Link>
        </div>
      </section>

      {/* Spread the Word */}
      <section className="py-16 bg-[#1A3A52]" data-testid="spread-the-word-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Share2 className="w-8 h-8 text-[#C67B5C] mx-auto mb-4" />
          <h2 className="font-serif text-2xl lg:text-3xl font-semibold text-white mb-3">
            Know a Writer Who'd Love This?
          </h2>
          <p className="text-white/60 text-base mb-8 max-w-xl mx-auto">
            Help us grow this community. Share Collectively Write with a friend who deserves a space where their words truly belong to them.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <button
              onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent('Join Collectively Write — a community where writers own 100% of their work. Share your poetry, fiction, screenplays & more.')}&url=${encodeURIComponent('https://collectivelywrite.com')}`, '_blank', 'width=600,height=400')}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm font-medium transition-colors"
              data-testid="share-twitter"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
              Share on X
            </button>
            <button
              onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent('https://collectivelywrite.com')}`, '_blank', 'width=600,height=400')}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm font-medium transition-colors"
              data-testid="share-facebook"
            >
              <Facebook className="w-4 h-4" />
              Share on Facebook
            </button>
            <button
              onClick={() => { window.location.href = `mailto:?subject=${encodeURIComponent('Check out Collectively Write!')}&body=${encodeURIComponent('Hey! I found this amazing writing community where writers own 100% of their work. Check it out: https://collectivelywrite.com')}`; }}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm font-medium transition-colors"
              data-testid="share-email"
            >
              <Mail className="w-4 h-4" />
              Send via Email
            </button>
            <button
              onClick={() => { navigator.clipboard.writeText('https://collectivelywrite.com'); }}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#C67B5C] hover:bg-[#B56A4B] text-white text-sm font-medium transition-colors"
              data-testid="share-copy-link"
            >
              <Link2 className="w-4 h-4" />
              Copy Link
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
